import {
  Key,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SavedChatDto } from '../../store/saved-chats/savedChatsDto';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  exportBubblesPlainText,
  selectChatBubbles,
  selectCtFeedback,
  selectSavedChatsList,
  selectSelectedChat,
  selectSupervisorNotes,
  setCtFeedback,
  setSelectedChat,
  setSupervisorNotes,
} from '../../store/saved-chats/savedChatsSlice';
import {
  Table,
  TableColumn,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Textarea,
  ModalFooter,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  SortDescriptor,
  ButtonGroup,
} from '@nextui-org/react';
import NavBar from '../base/NavBar';
import { selectUserName, selectUserRole } from '../../store/auth/authSlice';
import {
  getSavedChatBubbles,
  getSavedChats,
  updateSavedChat,
} from '../../store/saved-chats/savedChatsThunks';
import SavedChatBox from './SavedChatBox';
import AnimatePresence from '../base/AnimatePresence';
import { formatIsoToLocaleDateTime } from '../../util';
import { FaAngleDown, FaPencil, FaTrash, FaEye } from 'react-icons/fa6';
import Icon from '../base/Icon';
import MarkdownWrapper from '../base/MarkdownWrapper';
import ButtonExportTxt from '../base/ButtonExportTxt';
import {
  deleteChatsToDatabase,
  generate_trainee_feedback,
} from '../../store/chat/chatThunks';
import ButtonWithConfirmModal from '../base/ButtonWithConfirmModal';
import store from '../../store/store';
import type { Selection } from '@react-types/shared';

type TableSortDescriptor = {
  column: Key | null;
  direction: 'ascending' | 'descending' | null;
};

export default function SavedChatsView() {
  const dispatch = useAppDispatch();
  const savedChatsList = useAppSelector(selectSavedChatsList);
  const userRole = useAppSelector(selectUserRole);
  const ctFeedback = useAppSelector(selectCtFeedback);
  const supervisorNotes = useAppSelector(selectSupervisorNotes);
  const chatBubbles = useAppSelector(selectChatBubbles);
  const selectedChat = useAppSelector(selectSelectedChat);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [sortDescriptor, setSortDescriptor] = useState<TableSortDescriptor>({
    column: null,
    direction: null,
  });
  const [markdownFeedback, setMarkdownFeedback] = useState(ctFeedback);
  const userName = useAppSelector(selectUserName);
  const [textareaNotes, setTextAreaNotes] = useState(supervisorNotes);
  const {
    isOpen: isStageModalOpen,
    onOpen: onStageModalOpen,
    onOpenChange: onStageModalOpenChange,
  } = useDisclosure();
  const [stageInput, setStageInput] = useState('');
  const [stageModalChatId, setStageModalChatId] = useState<string | null>(null);

  useEffect(() => {
    setTextAreaNotes(supervisorNotes);
  }, [supervisorNotes]);

  useEffect(() => {
    setMarkdownFeedback(ctFeedback);
  }, [ctFeedback]);

  /* ---------------- FILTER STATE ---------------- */
  const [filters, setFilters] = useState<{
    status: 'all' | 'ongoing' | 'completed';
    users: Set<string>;
    reviewers: Set<string>;
    stages: Set<string>;
  }>({
    status: 'all',
    users: new Set(),
    reviewers: new Set(),
    stages: new Set(),
  });

  /* ---------------- FILTER OPTIONS ---------------- */
  const userOptions = useMemo(
    () =>
      Array.from(new Set(savedChatsList.map((c) => c.owner).filter(Boolean))),
    [savedChatsList]
  );

  const reviewerOptions = useMemo(
    () =>
      Array.from(
        new Set(savedChatsList.map((c) => c.reviewer).filter(Boolean))
      ),
    [savedChatsList]
  );

  const stageOptions = ['1', '2', '3', '4', '5'];

  /* ---------------- LOAD DATA ---------------- */
  useMemo(() => {
    if (userRole === 'admin' || userRole === 'user_sos') {
      dispatch(getSavedChats());
    }
  }, [userRole, dispatch]);

  /* ---------------- FILTERED CHATS ---------------- */
  const filteredChats = useMemo(() => {
    return savedChatsList.filter((chat) => {
      if (filters.status !== 'all' && chat.status !== filters.status)
        return false;
      if (filters.users.size > 0 && !filters.users.has(chat.owner))
        return false;
      if (
        filters.reviewers.size > 0 &&
        !filters.reviewers.has(chat.reviewer || '')
      )
        return false;
      if (filters.stages.size > 0 && !filters.stages.has(chat.stage || ''))
        return false;
      return true;
    });
  }, [savedChatsList, filters]);

  const sortedChats = useMemo(() => {
    if (!sortDescriptor.column) return filteredChats;

    const key = sortDescriptor.column as keyof SavedChatDto;
    const direction = sortDescriptor.direction === 'descending' ? -1 : 1;

    return [...filteredChats].sort((a, b) => {
      const v1 = a[key];
      const v2 = b[key];

      if (v1 == null) return 1;
      if (v2 == null) return -1;

      if (v1 < v2) return -1 * direction;
      if (v1 > v2) return 1 * direction;
      return 0;
    });
  }, [filteredChats, sortDescriptor]);

  /* ---------------- HANDLERS ---------------- */
  const handleDeleteChat = useCallback(
    (chat: SavedChatDto) => dispatch(deleteChatsToDatabase(chat.id)),
    [dispatch]
  );

  const handleFeedbackOpen = useCallback(
    (chat: SavedChatDto) => {
      dispatch(setSelectedChat(chat));
      dispatch(setSupervisorNotes(chat.notes || ''));
      if (!chat.feedback) {
        dispatch(
          generate_trainee_feedback({
            prompt_type: 'generate_feedback_CT_supervision',
            content: '',
            history_client: [],
            persist: false,
            chat_id: chat.chatId,
          })
        );
      } else {
        dispatch(setCtFeedback(chat.feedback));
      }
    },
    [dispatch]
  );

  const handleExportPlainText = useCallback(
    (chat: SavedChatDto) => {
      if (!chatBubbles?.length) {
        dispatch(getSavedChatBubbles(chat.chatId));
        setTimeout(() => {
          const bubbles = store.getState().savedChats.chatBubbles;
          if (bubbles?.length) {
            const match = chat.chatName?.match(/-\s(.*?)\s\(/);
            exportBubblesPlainText(bubbles, chat, match ? match[1] : '');
          }
        }, 500);
        return;
      }
    },
    [chatBubbles, dispatch]
  );

  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      if (keys === 'all') return;

      const chatId = Array.from(keys)[0]?.toString();
      if (!chatId) return;

      dispatch(getSavedChatBubbles(chatId));

      const savedChat = savedChatsList.find((chat) => chat.chatId === chatId);

      if (!savedChat) return;

      dispatch(setSelectedChat(savedChat));
      dispatch(setSupervisorNotes(savedChat.notes || ''));

      if (!savedChat.feedback) {
        dispatch(
          generate_trainee_feedback({
            prompt_type: 'generate_feedback_CT_supervision',
            content: '',
            history_client: [],
            persist: false,
            chat_id: chatId,
          })
        );
      } else {
        dispatch(setCtFeedback(savedChat.feedback));
      }
    },
    [dispatch, savedChatsList]
  );

  const handleUpdateChat =
    (onClose: () => void) =>
    (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      e.preventDefault();

      const form = new FormData(e.currentTarget);
      const feedback = markdownFeedback;
      const notes = form.get('notes')?.toString();
      const status =
        e.nativeEvent.submitter?.id === 'save-feedback'
          ? 'ongoing'
          : 'completed';
      if (feedback) {
        dispatch(
          updateSavedChat({
            chatId: selectedChat.chatId,
            owner: selectedChat.owner,
            chatName: selectedChat.chatName,
            feedback: feedback,
            reviewer: userName || '',
            status: status,
            stage: selectedChat.stage || '',
            notes: notes || '',
          })
        );
        dispatch(setCtFeedback(feedback));
        dispatch(setSupervisorNotes(notes || ''));
      }

      onClose();
    };

  const handleEditActorStage = useCallback(
    (savedChat: SavedChatDto) => {
      dispatch(setSelectedChat(savedChat));
      setStageModalChatId(savedChat.chatId);
      setStageInput(savedChat.stage || '');
      onStageModalOpen();
    },
    [onStageModalOpen, dispatch]
  );

  const handleSaveStage = () => {
    if (!stageModalChatId) return;

    dispatch(
      updateSavedChat({
        chatId: stageModalChatId,
        owner: selectedChat.owner,
        chatName: selectedChat.chatName,
        feedback: selectedChat.feedback,
        reviewer: selectedChat.reviewer || '',
        status: selectedChat.status,
        notes: selectedChat.notes || '',
        stage: stageInput,
      })
    );

    onStageModalOpenChange();
  };

  const renderCell = useCallback(
    (chat: SavedChatDto, columnKey: Key) => {
      const value = chat[columnKey as keyof SavedChatDto];

      switch (columnKey) {
        case 'created_at':
        case 'updated_at':
          return value ? formatIsoToLocaleDateTime(value) : '';
        case 'feedback':
          return (
            <Button
              variant="flat"
              startContent={
                <Icon icon={chat.status === 'completed' ? FaEye : FaPencil} />
              }
              onPress={() => {
                onOpen();
                handleFeedbackOpen(chat);
              }}
              className="feedback-btn"
            />
          );
        case 'stage':
          if (!value) {
            return (
              <Button
                color="default"
                variant="flat"
                startContent={<Icon icon={FaPencil} />}
                className="actor-stage-btn"
                onPress={() => handleEditActorStage(chat)}
              >
                {value || ''}
              </Button>
            );
          }
          return value;
        case 'id':
          return (
            <>
              <ButtonExportTxt
                className="saved-chat-export-btn"
                onPress={() => handleExportPlainText(chat)}
                isDisabled={!chat.feedback}
              />
              <ButtonWithConfirmModal
                onPress={() => handleDeleteChat(chat)}
                buttonContent={<Icon icon={FaTrash} />}
                confirmLabel="Delete"
                modalHeader={`Delete ${chat.chatName}?`}
                buttonProps={{
                  className: 'saved-chat-delete-btn',
                }}
                tooltipProps={{
                  content: 'Delete conversation',
                  placement: 'bottom',
                  color: 'danger',
                }}
              />
            </>
          );
        default:
          return value;
      }
    },
    [
      onOpen,
      handleDeleteChat,
      handleFeedbackOpen,
      handleExportPlainText,
      handleEditActorStage,
    ]
  );

  let feedbackFooter =
    selectedChat.status !== 'completed' ? (
      <ButtonGroup variant="solid">
        <Button type="submit" id="save-feedback">
          Save
        </Button>
        <Button type="submit" color="primary" id="create-feedback">
          Finalize Review
        </Button>
      </ButtonGroup>
    ) : (
      <></>
    );

  const isCompleted = selectedChat.status === 'completed';

  /* ---------------- UI ---------------- */
  return (
    <>
      <NavBar page="chats" />

      {/* FILTER BAR */}
      <div className="flex gap-3 p-4">
        {/* Status */}
        <Dropdown>
          <DropdownTrigger>
            <Button variant="flat" endContent={<Icon icon={FaAngleDown} />}>
              Status
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            selectionMode="single"
            selectedKeys={[filters.status]}
            onSelectionChange={(keys) =>
              setFilters((f) => ({ ...f, status: Array.from(keys)[0] as any }))
            }
          >
            <DropdownItem key="all">All chats</DropdownItem>
            <DropdownItem key="ongoing">Pending Review</DropdownItem>
            <DropdownItem key="completed">Reviewed</DropdownItem>
          </DropdownMenu>
        </Dropdown>

        {/* Multi-select filters */}
        {[
          { label: 'User', key: 'users', options: userOptions },
          { label: 'Reviewer', key: 'reviewers', options: reviewerOptions },
          { label: 'Stage', key: 'stages', options: stageOptions },
        ].map(({ label, key, options }) => (
          <Dropdown key={key}>
            <DropdownTrigger>
              <Button variant="flat" endContent={<Icon icon={FaAngleDown} />}>
                {label}
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              closeOnSelect={false}
              selectionMode="multiple"
              selectedKeys={filters[key as 'users']}
              onSelectionChange={(keys) =>
                setFilters((f) => ({
                  ...f,
                  [key]: new Set(keys as Set<string>),
                }))
              }
            >
              {options.map((o) => (
                <DropdownItem key={o}>{o}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        ))}

        <Button
          variant="light"
          onPress={() =>
            setFilters({
              status: 'all',
              users: new Set(),
              reviewers: new Set(),
              stages: new Set(),
            })
          }
        >
          Clear filters
        </Button>
      </div>

      {/* TABLE + CHAT */}
      <div className="flex gap-4">
        <Table
          aria-label="Saved Chats table"
          isHeaderSticky
          selectionMode="single"
          onSelectionChange={handleSelectionChange}
          sortDescriptor={
            sortDescriptor.column
              ? (sortDescriptor as SortDescriptor)
              : undefined
          }
          onSortChange={(d) => setSortDescriptor(d as TableSortDescriptor)}
        >
          <TableHeader
            columns={[
              { key: 'owner', label: 'USER', sortable: true },
              { key: 'chatName', label: 'CHAT NAME', sortable: true },
              { key: 'stage', label: 'STAGE', sortable: true },
              { key: 'created_at', label: 'CREATED', sortable: true },
              { key: 'feedback', label: 'FEEDBACK' },
              { key: 'reviewer', label: 'REVIEWER', sortable: true },
              { key: 'updated_at', label: 'REVIEW DATE', sortable: true },
              { key: 'id', label: 'ACTIONS' },
            ]}
          >
            {(col) => (
              <TableColumn key={col.key} allowsSorting={col.sortable}>
                {col.label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody items={sortedChats} emptyContent="No chats">
            {(item) => (
              <TableRow key={item.chatId}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>

        <AnimatePresence mode="wait">
          {selectedChat && (
            <SavedChatBox
              key={selectedChat.chatId}
              messages={chatBubbles}
              headerTitle={selectedChat.chatName}
            />
          )}
        </AnimatePresence>
      </div>

      {/* FEEDBACK MODAL */}
      <Modal
        size="xl"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        className="feedback-modal"
      >
        <ModalContent>
          {(onClose) => (
            <form onSubmit={handleUpdateChat(onClose)}>
              <ModalHeader>AI Care Assessment</ModalHeader>
              <ModalBody className="feedback-modal-body">
                <MarkdownWrapper extraClass="saved-chats-ct-feedback">
                  {markdownFeedback || '_Loading..._'}
                </MarkdownWrapper>
                <label htmlFor="notes-textarea">Supervisor Notes</label>
                <Textarea
                  id="notes-textarea"
                  name="notes"
                  value={textareaNotes}
                  onChange={(e) => dispatch(setSupervisorNotes(e.target.value))}
                  disabled={isCompleted}
                  style={{
                    width: '100%',
                    cursor: isCompleted ? 'not-allowed' : 'auto',
                  }}
                />
              </ModalBody>
              <ModalFooter>{feedbackFooter}</ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>

      <Modal isOpen={isStageModalOpen} onOpenChange={onStageModalOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Edit Training Stage</ModalHeader>
              <ModalBody>
                <input
                  type="text"
                  value={stageInput}
                  onChange={(e) => setStageInput(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Enter stage"
                />
              </ModalBody>
              <ModalFooter>
                <Button color="default" variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button color="primary" onPress={handleSaveStage}>
                  Save
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
